import styled from '@emotion/styled';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import React from 'react';
import { useParams } from 'react-router-dom';

import Spacer from '../base/spacer';
import UserNavigationMenu from '../settings/user-navigation-menu';

import * as GQL from '~/graphql/generated/client';
import { MedicalHeartrateReport, FileDocument } from '~c/components/base/icon';
import {
  LeftNavMenu,
  LeftNavMenuItem,
  LeftNavMenuGroup,
  LeftNavFlyoutMenuItem,
  useLeftNavContext,
} from '~c/components/base/left-nav';
import Text from '~c/components/base/text';
import { dashboardRouteUtil, useCaseRouteUtil } from '~c/routes';

interface Props {
  className?: string;
}

const UseCaseLeftNavImpl: React.FC<Props> = ({ className }) => {
  const { useCaseId } = useParams<{ useCaseId: string }>();
  const { matchRoute, navigateTo } = useCaseRouteUtil.useHelper();
  const dashboard = dashboardRouteUtil.useHelper();
  const [reportsExpanded, setReportsExpanded] = React.useState(false);

  const { state } = useLeftNavContext();
  const { currentRoute } = state;

  const { data } = GQL.useUseCaseDetailsQuery({
    variables: { useCaseId: useCaseId! },
    skip: !useCaseId,
  });

  const routePrefix = {
    [useCaseRouteUtil.routePrefix]: useCaseRouteUtil.routePrefix,
  };

  React.useEffect(() => {
    if (matchRoute('BASIC_REPORT') || matchRoute('ADVANCED_REPORT')) {
      setReportsExpanded(true);
    }
  }, [matchRoute]);

  const handleReportsClick = () => {
    setReportsExpanded(!reportsExpanded);
  };

  return (
    <LeftNavMenu
      className={className}
      name={routePrefix[currentRoute ?? '']}
      header={{
        showBackButton: true,
        onClick: () => {
          dashboard.navigateTo('COMPLIANCE');
        },
      }}
    >
      <LeftNavMenuItem
        selected={matchRoute('DASHBOARD')}
        icon={DashboardOutlinedIcon}
        onClick={() => navigateTo('DASHBOARD')}
      >
        <Text>{data?.useCaseDetails?.name ?? 'Use Case'}</Text>
      </LeftNavMenuItem>

      {/* Reports Menu Group */}
      <LeftNavMenuGroup
        icon={MedicalHeartrateReport}
        title="Reports"
        expandedIcon={ExpandLessIcon}
        collapsedIcon={ExpandMoreIcon}
        expanded={reportsExpanded}
        onToggle={handleReportsClick}
      >
        <LeftNavFlyoutMenuItem
          icon={MedicalHeartrateReport}
          selected={matchRoute('BASIC_REPORT')}
          onClick={() => navigateTo('BASIC_REPORT')}
        >
          Basic Reports
        </LeftNavFlyoutMenuItem>
        <LeftNavFlyoutMenuItem
          icon={FileDocument}
          selected={matchRoute('ADVANCED_REPORT')}
          onClick={() => navigateTo('ADVANCED_REPORT')}
        >
          Advanced Reports
        </LeftNavFlyoutMenuItem>
      </LeftNavMenuGroup>
      <Spacer className="grow" />
      <UserNavigationMenu />
    </LeftNavMenu>
  );
};

const UseCaseLeftNav = styled(UseCaseLeftNavImpl)`
  .grow {
    flex-grow: 1;
  }

  .userinfo {
    padding: 16px;
    text-transform: capitalize;
  }

  .submenu-container {
    padding-left: 12px;
    margin-top: 4px;
    margin-bottom: 8px;
  }
`;

export default UseCaseLeftNav;
